"use client";

import { cn } from "@beweegbaas/ui/lib/utils";
import { IconUpload } from "@tabler/icons-react";
import React, { useCallback, useEffect, useRef, useState } from "react";

export interface FileDropZoneProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "onChange"> {
  onChange?: (files: File[]) => void;
  inputProps?: React.ComponentProps<"input">;
  allowsMultiple?: boolean;
  isPending?: boolean;
}

export function FileDropZone({
  onChange,
  inputProps,
  allowsMultiple = false,
  isPending = false,
  className,
  children,
  ...props
}: FileDropZoneProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const hiddenInputRef = useRef<HTMLInputElement>(null);
  const dropZoneRef = useRef<HTMLDivElement>(null);

  const syncFilesToHiddenInput = useCallback((fileArray: File[]) => {
    if (!hiddenInputRef.current || fileArray.length === 0) return;

    const dataTransfer = new DataTransfer();
    fileArray.forEach((file) => {
      dataTransfer.items.add(file);
    });
    hiddenInputRef.current.files = dataTransfer.files;
  }, []);

  // Sync files to hidden input for form submission (backup sync in case files state changes externally)
  useEffect(() => {
    if (!hiddenInputRef.current || files.length === 0) return;
    syncFilesToHiddenInput(files);
  }, [files, syncFilesToHiddenInput]);

  const handleFiles = useCallback(
    (fileList: FileList | null) => {
      if (!fileList || fileList.length === 0) return;

      const fileArray = Array.from(fileList);
      // Sync files to hidden input synchronously before calling onChange
      syncFilesToHiddenInput(fileArray);
      setFiles(fileArray);
      onChange?.(fileArray);
    },
    [onChange, syncFilesToHiddenInput],
  );

  const handleDragOver = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      if (!isDragging) {
        setIsDragging(true);
      }
    },
    [isDragging],
  );

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    // Only set isDragging to false if we're leaving the drop zone itself
    if (!dropZoneRef.current?.contains(e.relatedTarget as Node)) {
      setIsDragging(false);
    }
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      handleFiles(e.dataTransfer.files);
    },
    [handleFiles],
  );

  const handleClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    // Don't trigger file input if clicking on interactive elements (links, buttons, etc.)
    const target = e.target as HTMLElement;
    const isInteractiveElement =
      target.tagName === "BUTTON" ||
      target.tagName === "A" ||
      target.tagName === "INPUT" ||
      target.tagName === "SELECT" ||
      target.tagName === "TEXTAREA" ||
      target.closest("button, a, input, select, textarea, [data-no-trigger]") !== null;

    if (isInteractiveElement) {
      return;
    }

    fileInputRef.current?.click();
  }, []);

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      handleFiles(e.target.files);
    },
    [handleFiles],
  );

  const { name, ...restInputProps } = inputProps || {};

  return (
    <div ref={dropZoneRef} className={cn("flex flex-col gap-2", className)} {...props}>
      {/* Visible file input for clicking */}
      <input
        ref={fileInputRef}
        type="file"
        multiple={allowsMultiple}
        style={{ display: "none" }}
        onChange={handleFileInputChange}
        accept={inputProps?.accept}
      />
      {/* Hidden input for form submission */}
      {name && (
        <input
          ref={hiddenInputRef}
          type="file"
          name={name}
          multiple={allowsMultiple}
          style={{ display: "none" }}
          {...restInputProps}
        />
      )}
      <div
        onClick={handleClick}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={cn(
          `relative flex w-full cursor-pointer flex-col items-center justify-center
          rounded-md border-2 border-dashed p-6 transition-colors`,
          isDragging ? "border-primary bg-primary/10" : "border-border hover:bg-muted",
          isPending && "pointer-events-none opacity-50",
        )}
      >
        {isPending && (
          <div
            className="bg-background/20 absolute inset-0 z-10 flex
              items-center justify-center rounded-md backdrop-blur-sm"
          >
            <div
              className="border-primary size-6 animate-spin rounded-full
                border-2 border-t-transparent"
              role="status"
              aria-label="Loading"
            />
          </div>
        )}
        {children}
      </div>
    </div>
  );
}

export interface FileDropZoneIconProps extends React.ComponentProps<"span"> {}

export function FileDropZoneIcon({ className, ...props }: FileDropZoneIconProps) {
  return (
    <span
      className={cn(
        `bg-accent/10 text-muted-foreground mb-4 flex size-10 items-center
        justify-center rounded-full`,
        className,
      )}
      {...props}
    >
      <IconUpload className="size-6" />
    </span>
  );
}

export interface FileDropZoneLabelProps extends React.ComponentProps<"div"> {}

export function FileDropZoneLabel({ children, className, ...props }: FileDropZoneLabelProps) {
  return (
    <div
      className={cn(
        "text-foreground/60 flex flex-wrap items-center justify-center gap-2",
        className,
      )}
      {...props}
    >
      {children}
    </div>
  );
}

export interface FileDropZoneFooterProps extends React.ComponentProps<"div"> {}

export function FileDropZoneFooter({ children, ...props }: FileDropZoneFooterProps) {
  return (
    <div className="text-muted-foreground flex justify-between gap-4 text-xs" {...props}>
      {children}
    </div>
  );
}

// Helper function to format file extensions from accept string
function useFormattedExtensions(accept?: string): string | null {
  if (!accept) {
    return null;
  }

  const acceptedFormats = accept.split(",").map((format) => format.trim());
  const extensions = acceptedFormats
    .map((format) => {
      if (format.startsWith(".")) {
        return format;
      }
      // Simple MIME type to extension mapping
      const mimeToExt: Record<string, string> = {
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": ".xlsx",
        "application/vnd.ms-excel": ".xls",
        "application/pdf": ".pdf",
        "image/jpeg": ".jpg",
        "image/png": ".png",
        "text/plain": ".txt",
      };
      return mimeToExt[format] || null;
    })
    .filter((ext): ext is string => ext !== null);

  const uniqueExtensions = [...new Set(extensions)].sort();
  return uniqueExtensions.join(", ");
}

export interface FileDropZoneSupportedFormatsProps extends Omit<
  React.ComponentProps<"span">,
  "children"
> {
  children: React.ReactNode | ((values: { formattedExtensions: string | null }) => React.ReactNode);
  inputProps?: React.ComponentProps<"input">;
}

export function FileDropZoneSupportedFormats({
  children,
  inputProps,
  ...props
}: FileDropZoneSupportedFormatsProps) {
  const accept = inputProps?.accept;
  const formattedExtensions = useFormattedExtensions(accept);

  if (!accept) {
    return null;
  }

  return (
    <span
      className="text-muted-foreground flex justify-between gap-4 text-xs
        font-normal"
      {...props}
    >
      {typeof children === "function" ? children({ formattedExtensions }) : children}
    </span>
  );
}

// Attach subcomponents to main component for dot notation usage
FileDropZone.Icon = FileDropZoneIcon;
FileDropZone.Label = FileDropZoneLabel;
FileDropZone.Footer = FileDropZoneFooter;
FileDropZone.SupportedFormats = FileDropZoneSupportedFormats;
